<template>
  <div class="team-preview-page">
    <TeamPreviewPopup :mode="previewMode" :player-data="effectivePlayerData" :match-info="effectiveMatchInfo"
      :team-meta="effectiveTeamMeta" @close="handleBack" @edit="handleEdit" @share="handleShare" @pts="handlePts"
      @refresh="handleRefresh" @playerClick="handlePlayerClick" />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import TeamPreviewPopup from './TeamPreviewPopup.vue'
import { getTeamDetail } from '@/api/team.js'
import { showToast } from '@/utils/message.js'
import { MatchStatus, getStandardRole } from '@/views/contests/matchesType.js'
import { useMatchStatus } from '@/composables/useMatchStatus.js'
import { openShare } from '@/composables/share/useShareState'

const props = defineProps({
  position: {
    type: String,
    default: 'edit',
  },
  sourceType: {
    type: String,
    default: '',
  },
  previewPayload: {
    type: Object,
    default: null,
  },
  matchId: {
    type: [String, Number],
    default: '',
  },
  teamId: {
    type: [String, Number],
    default: '',
  },
  playerData: {
    type: Array,
    default: null,
  },
  matchInfo: {
    type: Object,
    default: null,
  },
  isPopupMode: {
    type: Boolean,
    default: false,
  },
  autoTriggerShare: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])
const router = useRouter()
const route = useRoute()

const fetchedPlayers = ref([])
const fetchedTeamMeta = ref({})
const isLoading = ref(false)
const hasAutoTriggeredShare = ref(false)
let autoShareTimer = null

const routeMatchId = computed(() => route.params.mid || route.query.mid || route.query.matchId || '')
const routeTeamId = computed(() => route.params.teamId || route.query.teamId || '')
const effectiveMatchId = computed(() => props.matchId || routeMatchId.value || props.previewPayload?.mid || 0)
const effectiveTeamId = computed(() => props.teamId || routeTeamId.value || '')

const finalSourceType = computed(() => {
  if (props.sourceType) return props.sourceType
  if (props.previewPayload || props.playerData || props.matchInfo) return 'inline'
  return 'fetch'
})

const normalizeMatch = (rawMatch = {}, statusFallback = 0) => {
  if (!rawMatch || typeof rawMatch !== 'object') return {}
  const teama = rawMatch.teama || {}
  const teamb = rawMatch.teamb || {}
  const status = rawMatch.matchStatus || rawMatch.status || rawMatch.matchState || statusFallback || MatchStatus.NOT_STARTED
  return {
    ...rawMatch,
    mid: rawMatch.mid || effectiveMatchId.value || 0,
    teamAShort: teama.shortName || rawMatch.teamaShortName || rawMatch.teamaName || '',
    teamBShort: teamb.shortName || rawMatch.teambShortName || rawMatch.teambName || '',
    teamALogo: teama.logoUrl || rawMatch.teamaLogo || '',
    teamBLogo: teamb.logoUrl || rawMatch.teambLogo || '',
    teamaId: teama.teamId || rawMatch.teamaId || rawMatch.taid || '',
    teambId: teamb.teamId || rawMatch.teambId || rawMatch.tbid || '',
    timestampStart: rawMatch.timestampStart || rawMatch.matchStartTime || 0,
    status,
    matchStatus: status,
  }
}

const normalizePlayers = (rawPlayers = []) => {
  if (!Array.isArray(rawPlayers)) return []
  return rawPlayers.map((player) => {
    const position = getStandardRole(player.playerRole || player.role || player.position || '')
    const isCaptain = player.playerRoleType !== undefined ? player.playerRoleType === 1 : !!player.isCaptain
    const isViceCaptain = player.playerRoleType !== undefined ? player.playerRoleType === 2 : !!player.isViceCaptain
    return {
      pid: player.pid || player.id,
      id: player.id || player.pid,
      name: player.shortName || player.playerName || player.title || 'Unknown',
      fullName: player.title || player.firstName || player.shortName || '',
      avatar: player.logoUrl || player.thumbUrl || player.playerAvatar || '',
      team: player.teamShortName || player.teamName || '',
      teamId: player.tid || player.teamId,
      position,
      points: player.point ?? player.points ?? 0,
      rating: player.rating ?? player.credits ?? 0,
      isCaptain,
      isViceCaptain,
      starting11: player.starting11 || 0,
      substitute: player.substitute || 0,
      dreamTeam: player.dreamTeam || 0,
      _raw: player,
    }
  })
}

const normalizeTeamDetailResponse = (raw) => {
  if (Array.isArray(raw)) {
    return {
      players: raw,
      meta: {},
    }
  }
  if (Array.isArray(raw?.players) || Array.isArray(raw?.playerList)) {
    return {
      players: raw.players || raw.playerList || [],
      meta: {
        userId: raw.userId ?? '',
        teamName: raw.teamName ?? '',
        teamNo: raw.teamNo ?? '',
      },
    }
  }
  console.warn('[AppTeamPreview] Unexpected team detail response shape:', raw)
  return {
    players: [],
    meta: {},
  }
}

const {
  matchInfo: hookMatchInfo,
  matchStatus,
  loading: hookLoading,
} = useMatchStatus(() => Number(effectiveMatchId.value || 0))

watch(hookLoading, (val) => {
  if (finalSourceType.value === 'fetch') {
    isLoading.value = val
  }
})

const previewMode = computed(() => {
  return props.position === 'other' ? 'other-team' : 'my-team'
})

const payloadMatch = computed(() => {
  if (!props.previewPayload) return null
  return props.previewPayload.match || props.previewPayload
})

const effectiveMatchInfo = computed(() => {
  if (finalSourceType.value === 'inline') {
    const raw = payloadMatch.value || props.matchInfo || {}
    return normalizeMatch(raw)
  }
  return normalizeMatch(hookMatchInfo.value || {}, matchStatus.value)
})

const effectivePlayerData = computed(() => {
  if (finalSourceType.value === 'inline') {
    const raw = props.previewPayload?.players || props.playerData || []
    return normalizePlayers(raw)
  }
  return normalizePlayers(fetchedPlayers.value)
})

const effectiveTeamMeta = computed(() => {
  if (finalSourceType.value === 'inline') {
    const payload = props.previewPayload || {}
    return {
      userId: payload.userId ?? '',
      teamName: payload.teamName ?? '',
      teamNo: payload.teamNo ?? '',
      userTeamId: payload.userTeamId ?? payload.teamId ?? effectiveTeamId.value ?? '',
    }
  }
  return {
    ...fetchedTeamMeta.value,
    userTeamId: effectiveTeamId.value || fetchedTeamMeta.value?.userTeamId || '',
  }
})

const fetchTeamDetail = async () => {
  if (finalSourceType.value !== 'fetch' || !effectiveTeamId.value) return
  try {
    isLoading.value = true
    const res = await getTeamDetail({ tid: effectiveTeamId.value })
    if (res && res.resultData) {
      const normalized = normalizeTeamDetailResponse(res.resultData)
      fetchedPlayers.value = normalized.players
      fetchedTeamMeta.value = normalized.meta
    } else {
      fetchedPlayers.value = []
      fetchedTeamMeta.value = {}
    }
  } catch (error) {
    console.error('Failed to fetch team detail:', error)
    fetchedPlayers.value = []
    fetchedTeamMeta.value = {}
  } finally {
    isLoading.value = false
  }
}

const handlePlayerClick = (player) => {
  if (effectiveMatchInfo.value?.status === MatchStatus.NOT_STARTED) {
    showToast.info('Player points will be updated only after the match starts')
    return false
  }
  if (!effectiveTeamId.value) {
    showToast.info('Team details are not available yet')
    return false
  }
  const mid =
    effectiveMatchInfo.value?.mid ||
    route.query.mid ||
    route.query.matchId ||
    route.params.mid

  if (!mid) {
    showToast('Match ID missing')
    return
  }
  router.push({
    name: 'PlayerScoreDetail',
    params: {
      playerId: player?.pid,
      userTeamId: effectiveTeamId.value,
      mid,
    },
  })
}

const handleBack = () => {
  if (props.isPopupMode) {
    emit('close')
  } else {
    router.back()
  }
}

const handleEdit = () => {
  const currentMid = effectiveMatchInfo.value?.mid || effectiveMatchId.value
  if (!currentMid) {
    showToast('Cannot edit: Match ID not found')
    return
  }
  router.push({
    name: 'EditMyTeam',
    params: {
      mid: currentMid,
      tid: effectiveTeamId.value,
    },
  })
}

const handleShare = () => {
  // openShare('team', {
  //   matchInfo: effectiveMatchInfo.value,
  //   players: effectivePlayerData.value,
  //   teamMeta: effectiveTeamMeta.value,
  //   matchId: effectiveMatchId.value,
  //   teamId: effectiveTeamId.value,
  //   title: `${effectiveMatchInfo.value?.teamAShort || ''} vs ${effectiveMatchInfo.value?.teamBShort || ''}`.trim(),
  //   desc: `Team T${effectiveTeamMeta.value?.teamNo || '--'} Preview`,
  // })
  showToast.info('The sharing function is temporarily unavailable. Please stay tuned.')
}

const isShareDataReady = computed(() => {
  const hasMatchIdentity = !!effectiveMatchInfo.value?.mid
  const hasMatchTeams = !!(effectiveMatchInfo.value?.teamAShort || effectiveMatchInfo.value?.teamBShort)
  const hasTeamIdentity = !!effectiveTeamId.value
  // 分享前至少保证比赛基础信息和队伍ID可用，避免弹层出现空数据
  return hasTeamIdentity && hasMatchIdentity && hasMatchTeams && !isLoading.value
})

watch(
  () => [
    props.autoTriggerShare,
    isShareDataReady.value,
    effectiveMatchInfo.value?.teamAShort,
    effectiveMatchInfo.value?.teamBShort,
    effectiveMatchInfo.value?.mid,
  ],
  ([autoShare, ready]) => {
    if (!autoShare || !ready || hasAutoTriggeredShare.value) return
    hasAutoTriggeredShare.value = true
    if (autoShareTimer) {
      clearTimeout(autoShareTimer)
      autoShareTimer = null
    }
    // 让比赛信息/队伍信息稳定后再自动拉起分享弹层
    autoShareTimer = setTimeout(() => {
      handleShare()
      autoShareTimer = null
    }, 420)
  },
  { immediate: true },
)
const handlePts = () => {
  router.push({
    name: 'RulesDetail',
  })
}


const handleRefresh = () => {
  fetchTeamDetail()
}

onMounted(() => {
  fetchTeamDetail()
})

onUnmounted(() => {
  if (autoShareTimer) {
    clearTimeout(autoShareTimer)
    autoShareTimer = null
  }
})
</script>

<style lang="less" scoped>
.team-preview-page {
  min-height: 100vh;
  background: #1a1a1a;
}
</style>
